import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Home } from "lucide-react";

const SHOWN_KEY = "lupuslogix_post_install_shown";
const INSTALLED_KEY = "lupuslogix_pwa_installed";

/**
 * Single, one-time confirmation shown right after the OS finishes installing
 * the PWA. Replaces the mid-flow "Install banner" + "Install first" + drawer
 * helper pileup. Fires on the `appinstalled` event (or if the installed flag
 * is already present and we haven't shown it yet), keyed so it never repeats.
 */
export function PostInstallConfirmationDialog() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const alreadyShown = () => {
      try { return localStorage.getItem(SHOWN_KEY) === "true"; } catch { return false; }
    };
    const markShown = () => {
      try { localStorage.setItem(SHOWN_KEY, "true"); } catch { /* ignore */ }
    };
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      // @ts-expect-error legacy iOS
      window.navigator.standalone === true;

    if (alreadyShown()) return;

    // If the user is already inside the standalone PWA, the OS install
    // clearly finished on a prior session — no need for a fresh confirmation.
    if (isStandalone) {
      markShown();
      return;
    }

    const handleInstalled = () => {
      if (alreadyShown()) return;
      markShown();
      try { localStorage.setItem(INSTALLED_KEY, "true"); } catch { /* ignore */ }
      setOpen(true);
    };

    window.addEventListener("appinstalled", handleInstalled);
    return () => window.removeEventListener("appinstalled", handleInstalled);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <DialogTitle className="text-center text-xl">
            You're all set — LupusLogix is installed
          </DialogTitle>
          <DialogDescription className="text-center">
            A few quick tips so reminders and calendar sync work reliably:
          </DialogDescription>
        </DialogHeader>

        <ol className="space-y-3 text-sm px-1">
          <li className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">1</span>
            <span>
              <strong>Find the icon.</strong> On many Android phones the app lands in your{" "}
              <strong>app drawer</strong> first. Swipe up from the home screen, find{" "}
              <strong>LupusLogix</strong>, then long-press and tap{" "}
              <em>"Add to Home screen"</em>.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">2</span>
            <span>
              <strong>Close this browser tab</strong> and open LupusLogix from the new{" "}
              <Home className="inline h-4 w-4 -mt-0.5" /> home screen icon from now on.
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">3</span>
            <span>
              <strong>Push reminders & Google Calendar sync</strong> only run inside the installed app — not in the browser tab.
            </span>
          </li>
        </ol>

        <DialogFooter>
          <Button onClick={() => setOpen(false)} className="w-full">
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default PostInstallConfirmationDialog;